import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SystemTerminal from '../layout/SystemTerminal';
import { formatWorkspaceScanStatus } from '../../utils/investigationWorkspaceUtils';
import { computeWorkspaceRiskLevel } from '../../utils/workspaceRiskLevel';

/**
 * Compact live status strip for Home investigation workspace.
 * Alias: WorkspaceStatusBar (develop UX naming).
 *
 * Risk is shown only here as RISK LEVEL (no floating Severity strip).
 */
export default function InvestigationStatsBar({
  className = 'mt-4',
  loading,
  penalties = [],
  displayResults = [],
  resultsCount = 0,
  hasVerifiedIdentity = false,
  scanFinished = false,
  finalReport = false,
  trustScore = '--',
  trustLabel = 'Pending',
  trustColorClass = 'text-slate-400',
  logs = [],
  logEndRef,
  sticky = true,
}) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [logsOpen, setLogsOpen] = useState(false);
  const [countPulse, setCountPulse] = useState(false);
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const prevCountRef = useRef(resultsCount);

  useEffect(() => {
    if (loading) {
      if (startRef.current == null) {
        startRef.current = performance.now();
        setElapsedMs(0);
      }
      const tick = (now) => {
        if (startRef.current != null) setElapsedMs(now - startRef.current);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (startRef.current != null) {
      setElapsedMs(Math.max(0, performance.now() - startRef.current));
      startRef.current = null;
    }
    return undefined;
  }, [loading]);

  useEffect(() => {
    if (logsOpen && logEndRef?.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs.length, logsOpen, logEndRef]);

  useEffect(() => {
    if (resultsCount > prevCountRef.current) {
      setCountPulse(true);
      const t = window.setTimeout(() => setCountPulse(false), 520);
      prevCountRef.current = resultsCount;
      return () => window.clearTimeout(t);
    }
    prevCountRef.current = resultsCount;
    return undefined;
  }, [resultsCount]);

  const elapsedLabel = useMemo(() => {
    const totalSec = Math.floor(elapsedMs / 1000);
    const mm = Math.floor(totalSec / 60);
    const ss = totalSec % 60;
    const cs = Math.floor((elapsedMs % 1000) / 10);
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
  }, [elapsedMs]);

  const logCount = logs?.length || 0;
  const completedLike = Boolean(scanFinished || finalReport || (!loading && hasVerifiedIdentity));

  const statusLabel = formatWorkspaceScanStatus({
    loading,
    completed: completedLike || finalReport,
    scanFinished,
    finalReport,
    elapsedMs,
  });

  const statusTone = loading
    ? 'text-emerald-100'
    : completedLike || finalReport
      ? 'text-cyan-200'
      : 'text-slate-400';

  const risk = useMemo(
    () => computeWorkspaceRiskLevel(displayResults, { penalties }),
    [displayResults, penalties],
  );

  const showRiskChip = Boolean(finalReport || scanFinished || (!loading && resultsCount > 0));

  return (
    <section
      className={`${sticky ? 'home-stats-bar-sticky' : 'home-stats-bar-inline'} relative z-[2] animate-in fade-in duration-300 ${className} ${finalReport ? 'rounded-xl ring-1 ring-cyan-500/20' : ''} ${loading ? 'rounded-xl ring-2 ring-emerald-400/45' : ''}`}
      aria-label="Scan metrics"
      data-testid="workspace-status-bar"
      data-scanning={loading ? 'true' : undefined}
    >
      <div
        className={`flex flex-nowrap items-center justify-between gap-2 overflow-x-auto rounded-lg border px-2 py-1 font-mono text-[11px] uppercase tracking-wide shadow-lg shadow-black/20 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-4 sm:rounded-xl sm:overflow-visible sm:px-4 sm:py-2.5 sm:text-xs sm:tracking-wider sm:flex-wrap sm:gap-y-2 ${
          loading
            ? 'border-emerald-400/50 bg-emerald-950/55'
            : 'border-white/10 bg-slate-900/90'
        }`}
        data-testid="workspace-status-inner"
      >
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-x-2 sm:gap-x-4">
          <span className="hidden items-center gap-2 text-slate-400 sm:flex" data-testid="workspace-trust">
            Trust
            <span className={`text-sm font-black tabular-nums normal-case ${trustColorClass}`}>{trustScore}</span>
            <span className="hidden max-w-[8rem] truncate normal-case tracking-normal text-slate-400 lg:inline">
              {trustLabel}
            </span>
          </span>
          <span className="hidden h-4 w-px bg-white/10 sm:block" aria-hidden />
          <span className="flex shrink-0 items-center gap-1.5 text-slate-400 sm:gap-2" data-testid="workspace-runtime">
            <span className="sm:hidden">Timer</span>
            <span className="hidden sm:inline">Runtime</span>
            <span className="text-sm font-black tabular-nums text-slate-100">{elapsedLabel}</span>
          </span>
          <span className="hidden h-4 w-px bg-white/10 sm:block" aria-hidden />
          <span
            className={`hidden items-center gap-2 rounded-lg px-2.5 py-1 font-black tracking-[0.14em] sm:inline-flex ${statusTone} ${
              loading
                ? 'animate-pulse bg-emerald-500/25 ring-1 ring-emerald-400/70 shadow-[0_0_18px_rgba(52,211,153,0.4)]'
                : ''
            }`}
            data-testid="workspace-scan-status"
          >
            {loading ? (
              <span
                className="inline-block h-2 w-2 animate-ping rounded-full bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.95)]"
                aria-hidden
              />
            ) : null}
            {statusLabel}
          </span>
          <span className="hidden h-4 w-px bg-white/10 sm:block" aria-hidden />
          <span
            className={`inline-flex shrink-0 items-center gap-1.5 font-semibold text-slate-300 transition ${
              countPulse ? 'home-results-count-pulse text-cyan-300' : ''
            }`}
            data-testid="workspace-results-count"
            aria-live="polite"
          >
            <span className="sm:hidden">Results</span>
            <span className="hidden sm:inline">RESULTS:</span>{' '}
            <span className="tabular-nums text-cyan-200">{resultsCount}</span>
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {showRiskChip ? (
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-black tracking-[0.1em] sm:gap-1.5 sm:px-2.5 sm:text-[11px] sm:tracking-[0.12em] ${risk.toneClass}`}
              data-testid="workspace-risk-level"
              title={risk.level === 'CRITICAL' ? 'Aggregated investigation risk' : 'No critical risk pillars'}
            >
              <span aria-hidden>{risk.level === 'CRITICAL' ? '🔴' : '🟢'}</span>
              <span className="sm:hidden">
                {risk.level === 'CRITICAL' ? `ALERT (${risk.totalThreats})` : 'CLEAN'}
              </span>
              <span className="hidden sm:inline">{risk.label}</span>
            </span>
          ) : null}

          <button
            type="button"
            onClick={() => setLogsOpen((o) => !o)}
            aria-expanded={logsOpen}
            className="hidden items-center gap-1.5 rounded-lg px-2 py-1 text-[9px] font-bold text-slate-500 transition hover:bg-white/5 hover:text-slate-300 sm:flex"
          >
            Logs
            <span className="tabular-nums">{logCount}</span>
            {logsOpen ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
          </button>
        </div>
      </div>

      {logsOpen ? (
        <div className="mt-2 hidden h-[min(12rem,32vh)] min-h-[100px] overflow-hidden rounded-xl border border-white/10 bg-slate-950/60 sm:block">
          <SystemTerminal logs={logs} logEndRef={logEndRef} hideLabel className="h-full border-0 bg-transparent" />
        </div>
      ) : null}
    </section>
  );
}

export { InvestigationStatsBar as WorkspaceStatusBar };
